import React from "react";
import { AutoComplete, Card, Collapse, DatePicker, Descriptions, Divider, Flex, Form, Select, Typography } from "antd";
import dayjs from "dayjs";

/**
 * "Project Requirements" card on the lead form.
 *
 * The key fields are editable form items under `requirement_fields`, so staff
 * can fill them in on any lead (e.g. from a call). Leads submitted through the
 * website's /enquiry form also carry the client's full `answers`, shown
 * read-only below — they are the client's own words, not CRM data.
 *
 * `answers` is an ordered list of sections, each carrying its own labels, so
 * it renders whatever the form asked at the time.
 */

// Same choices as the website form (nodejs/teson/components/requirements-form.jsx).
const toOptions = (values) => values.map((value) => ({ value, label: value }));

const PROJECT_TYPES = toOptions(["Website", "E-commerce", "Mobile app", "Business software",
    "Integration/Automation/AI", "Maintenance/Takeover", "Other"]);
const INDUSTRIES = toOptions(["Healthcare", "Education", "Manufacturing", "Retail", "Pharma",
    "Real Estate", "Hospitality", "Logistics", "Finance", "Other"]);
const BUDGETS = toOptions(["Under 1L", "1L to 3L", "3L to 10L", "10L to 25L", "Above 25L", "Not decided"]);
const HEARD_FROM = toOptions(["Referral", "Google", "LinkedIn", "Instagram", "Existing client"]);
const DEADLINE = [{ value: true, label: "Yes" }, { value: false, label: "No" }];

const DATE_FIELDS = ["start_date", "launch_date"];
const TEXT_FIELDS = ["project_type", "industry", "budget", "heard_from"];

/** API requirement -> form values for `requirement_fields`. */
export const requirementToFormValues = (requirement) => {
    const values = {};
    [...TEXT_FIELDS, "fixed_deadline"].forEach((key) => { values[key] = requirement?.[key] ?? undefined; });
    DATE_FIELDS.forEach((key) => { values[key] = requirement?.[key] ? dayjs(requirement[key]) : null; });
    return values;
};

/** Form values -> `requirement_data` payload. */
export const formValuesToRequirement = (values = {}) => {
    const payload = {};
    TEXT_FIELDS.forEach((key) => { payload[key] = values[key] || ""; });
    DATE_FIELDS.forEach((key) => { payload[key] = values[key] ? dayjs(values[key]).format("YYYY-MM-DD") : null; });
    payload.fixed_deadline = typeof values.fixed_deadline === "boolean" ? values.fixed_deadline : null;
    return payload;
};

const SubmittedAnswers = ({ requirement }) => {
    const answers = requirement?.answers || [];
    if (!answers.length) return null;

    const items = answers.map((section) => ({
        key: section.key || section.title,
        label: (
            <Flex justify="space-between">
                <Typography.Text strong>{section.title}</Typography.Text>
                <Typography.Text type="secondary">{section.items.length} answers</Typography.Text>
            </Flex>
        ),
        children: (
            <Descriptions
                column={1}
                size="small"
                bordered
                labelStyle={{ width: "40%", verticalAlign: "top" }}
                items={section.items.map((item) => ({
                    key: item.key || item.label,
                    label: item.label,
                    children: (
                        <Typography.Paragraph style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}>
                            {/^https?:\/\//.test(item.value) ? (
                                <a href={item.value} target="_blank" rel="noopener noreferrer">{item.value}</a>
                            ) : item.value}
                        </Typography.Paragraph>
                    ),
                }))}
            />
        ),
    }));

    return (
        <>
            <Divider orientation="left" plain style={{ marginTop: 8 }}>
                Client's answers · submitted {dayjs(requirement.created_at).format("D MMM YYYY, h:mm a")}
            </Divider>
            <Collapse items={items} size="small" />
        </>
    );
};

const LeadRequirements = ({ requirement }) => (
    <Card title="Project Requirements" bordered={false}>
        <Form.Item label="Project Type" name={["requirement_fields", "project_type"]}>
            <Select allowClear placeholder="Select project type" options={PROJECT_TYPES} />
        </Form.Item>
        <Form.Item label="Industry" name={["requirement_fields", "industry"]}>
            <Select allowClear showSearch placeholder="Select industry" options={INDUSTRIES} />
        </Form.Item>
        <Form.Item label="Budget" name={["requirement_fields", "budget"]}>
            <Select allowClear placeholder="Select budget" options={BUDGETS} />
        </Form.Item>
        <Form.Item label="Start Date" name={["requirement_fields", "start_date"]}>
            <DatePicker format="DD MMM YYYY" />
        </Form.Item>
        <Form.Item label="Launch Date" name={["requirement_fields", "launch_date"]}>
            <DatePicker format="DD MMM YYYY" />
        </Form.Item>
        <Form.Item label="Fixed Deadline" name={["requirement_fields", "fixed_deadline"]}>
            <Select allowClear placeholder="Select" options={DEADLINE} />
        </Form.Item>
        {/* Free text with suggestions: the website form allows "Other: …". */}
        <Form.Item label="Heard From" name={["requirement_fields", "heard_from"]}>
            <AutoComplete allowClear placeholder="e.g. Google, Referral" options={HEARD_FROM}
                filterOption={(input, option) => option.value.toLowerCase().includes(input.toLowerCase())} />
        </Form.Item>
        <SubmittedAnswers requirement={requirement} />
    </Card>
);

export default LeadRequirements;
